// src/pages/kanban/TableroKanbanPage.tsx
import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, Badge, Spinner } from '@/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  ArrowLeft,
  Settings,
  RefreshCw,
  Star,
  Share2,
  LayoutGrid,
} from 'lucide-react';
import {
  useTablero,
  useMoverTarjeta,
  useColapsarColumna,
  useAgrupaciones,
  useUpdateTablero,
} from '@/hooks/queries/useKanban';
import { ColumnaKanban } from './components/ColumnaKanban';
import { ConfigurarTableroModal } from './components/ConfigurarTableroModal';
import { AgrupacionKanban, OrdenKanban } from '@/types/kanban';
import type { TarjetaKanban as TarjetaKanbanType } from '@/types/kanban';

const AGRUPACION_LABELS: Record<AgrupacionKanban, string> = {
  [AgrupacionKanban.ESTADO]: 'Estado',
  [AgrupacionKanban.PRIORIDAD]: 'Prioridad',
  [AgrupacionKanban.ASIGNADO]: 'Asignado',
  [AgrupacionKanban.CLIENTE]: 'Cliente',
  [AgrupacionKanban.TIPO]: 'Tipo',
  [AgrupacionKanban.CATEGORIA]: 'Categoria',
  [AgrupacionKanban.MODULO_ERP]: 'Modulo ERP',
};

const ORDEN_LABELS: Record<OrdenKanban, string> = {
  [OrdenKanban.MANUAL]: 'Manual',
  [OrdenKanban.PRIORIDAD]: 'Prioridad',
  [OrdenKanban.FECHA_CREACION]: 'Fecha creación',
  [OrdenKanban.FECHA_LIMITE]: 'Fecha límite',
  [OrdenKanban.ULTIMO_MOVIMIENTO]: 'Último movimiento',
};

export function TableroKanbanPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: tableroData, isLoading, refetch, error } = useTablero(id!);
  const moverTarjeta = useMoverTarjeta();
  const colapsarColumna = useColapsarColumna();
  const updateTablero = useUpdateTablero();

  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [draggingTarjeta, setDraggingTarjeta] = useState<TarjetaKanbanType | null>(null);

  const tablero = tableroData?.tablero;
  const columnas = tableroData?.columnas || [];

  // Obtener agrupaciones disponibles
  const { data: agrupaciones } = useAgrupaciones(tablero?.tipo);

  const handleDragStart = useCallback((_e: React.DragEvent, tarjeta: TarjetaKanbanType) => {
    setDraggingTarjeta(tarjeta);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent, columnaDestino: string) => {
      e.preventDefault();
      setDraggingTarjeta(null);

      const data = e.dataTransfer.getData('application/json');
      if (!data) return;

      try {
        const { entidad_tipo, entidad_id, tablero_id } = JSON.parse(data);

        if (tablero_id !== id) return;

        // Encontrar la posicion (al final de la columna destino)
        const columnaTarget = columnas.find((c) => c.valor === columnaDestino);
        const posicion = columnaTarget ? columnaTarget.tarjetas.length : 0;

        await moverTarjeta.mutateAsync({
          tableroId: id!,
          data: {
            entidad_tipo,
            entidad_id,
            columna_destino: columnaDestino,
            posicion,
          },
        });
      } catch (err) {
        console.error('Error al mover tarjeta:', err);
      }
    },
    [id, columnas, moverTarjeta]
  );

  const handleColapsar = useCallback(
    async (columna: string, colapsada: boolean) => {
      await colapsarColumna.mutateAsync({
        tableroId: id!,
        columna,
        colapsada,
      });
    },
    [id, colapsarColumna]
  );

  const handleAgrupacionChange = async (value: AgrupacionKanban) => {
    if (!tablero) return;
    await updateTablero.mutateAsync({
      id: tablero.id,
      data: { agrupacion: value },
    });
  };

  const handleOrdenChange = async (value: OrdenKanban) => {
    if (!tablero) return;
    await updateTablero.mutateAsync({
      id: tablero.id,
      data: { orden_tarjetas: value },
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !tableroData) {
    return (
      <div className="text-center py-12">
        <Card className="p-8 max-w-md mx-auto">
          <LayoutGrid className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Tablero no encontrado
          </h3>
          <p className="text-gray-500 mb-4">
            El tablero que buscas no existe o no tienes acceso.
          </p>
          <Button variant="ghost" onClick={() => navigate('/kanban')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a tableros
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-start justify-between flex-shrink-0">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/kanban')}
            className="mt-1"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{tablero!.nombre}</h1>
              {tablero!.es_default && (
                <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
              )}
              {tablero!.es_compartido && (
                <Badge className="flex items-center gap-1">
                  <Share2 className="h-3 w-3" />
                  Compartido
                </Badge>
              )}
            </div>
            {tablero!.descripcion && (
              <p className="text-gray-500 mt-1">{tablero!.descripcion}</p>
            )}
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2">
          {/* Selector agrupacion */}
          <Select
            value={tablero!.agrupacion}
            onValueChange={(value) => handleAgrupacionChange(value as AgrupacionKanban)}
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Agrupar por" />
            </SelectTrigger>
            <SelectContent>
              {(agrupaciones || Object.keys(AGRUPACION_LABELS)).map((agrupacion) => {
                const key = typeof agrupacion === 'string' ? agrupacion : agrupacion.valor;
                return (
                  <SelectItem key={key} value={key}>
                    {AGRUPACION_LABELS[key as AgrupacionKanban] || key}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {/* Selector orden */}
          <Select
            value={tablero!.orden_tarjetas}
            onValueChange={(value) => handleOrdenChange(value as OrdenKanban)}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Ordenar por" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(ORDEN_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="secondary" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>

          <Button onClick={() => setIsConfigModalOpen(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Configurar
          </Button>
        </div>
      </div>

      {/* Info */}
      <div className="flex items-center gap-4 text-sm text-gray-500 flex-shrink-0">
        <span>Total: {tableroData.total_tarjetas} tarjetas</span>
        <span>|</span>
        <span>Tipo: {tablero!.tipo === 'TICKETS' ? 'Tickets' : 'Actividades'}</span>
      </div>

      {/* Kanban Board */}
      {columnas.length === 0 ? (
        <Card className="p-12 flex-1">
          <div className="text-center">
            <LayoutGrid className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Sin columnas para mostrar
            </h3>
            <p className="text-gray-500 mb-4">
              No hay datos disponibles para la agrupacion seleccionada.
            </p>
          </div>
        </Card>
      ) : (
        <div className="flex-1 overflow-x-auto">
          <div className="flex gap-4 pb-4 min-h-[400px]">
            {columnas
              .filter((col) => {
                const config = tablero!.columnas_config?.find((c) => c.valor === col.valor);
                return !config?.es_oculta;
              })
              .map((columna) => (
                <ColumnaKanban
                  key={columna.valor}
                  columna={columna}
                  tableroId={id!}
                  tamanoTarjeta={(tablero!.tamano_tarjeta as 'compacto' | 'normal' | 'expandido') || 'normal'}
                  onDrop={handleDrop}
                  onDragStart={handleDragStart}
                  onColapsar={handleColapsar}
                />
              ))}
          </div>
        </div>
      )}

      {/* Drag indicator */}
      {draggingTarjeta && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg z-50">
          Arrastrando: {draggingTarjeta.titulo}
        </div>
      )}

      {/* Dialog configuracion */}
      {tableroData && (
        <ConfigurarTableroModal
          open={isConfigModalOpen}
          onClose={() => setIsConfigModalOpen(false)}
          tableroData={tableroData}
        />
      )}
    </div>
  );
}
